import { FC } from 'react'
import {
  EditButton, Show, Tab, TabbedShowLayout,
  TopToolbar, useGetList, useRecordContext, useTranslate,
} from 'react-admin'
import { Box, Typography } from '@mui/material'
import {
  DetailCard, InfoRow, StatCard, StatusChip, formatDateTime,
} from '../../components'
import { xxllncColor } from '../../layout/theme/color'
import { fadeSlideIn } from '../../components/dashboard'
import {
  ApplicationHeaderOut, ApplicationOut, McpServerOut, ProxyOut,
} from '../../types/apiRequests'
import ApplicationEnvironmentTab from './ApplicationEnvironmentTab'
import ApplicationHeaderTab from './ApplicationHeaderTab'
import { AUTH_TYPE_KEYS } from './authTypeLabels'

const ShowActions: FC = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
)

const DetailsTab: FC = () => {
  const record = useRecordContext<ApplicationOut>()
  const translate = useTranslate()

  const { total: mcpCount } = useGetList<McpServerOut>(
    'mcp_server',
    // eslint-disable-next-line camelcase
    { pagination: { page: 1, perPage: 1 }, filter: { application_id: record?.id } },
    { enabled: !!record?.id },
  )
  const { total: proxyCount } = useGetList<ProxyOut>(
    'proxy',
    // eslint-disable-next-line camelcase
    { pagination: { page: 1, perPage: 1 }, filter: { application_id: record?.id } },
    { enabled: !!record?.id },
  )
  const { total: headerCount } = useGetList<ApplicationHeaderOut>(
    'application_header',
    // eslint-disable-next-line camelcase
    { pagination: { page: 1, perPage: 1 }, filter: { application_id: record?.id } },
    { enabled: !!record?.id },
  )

  if (!record) return null

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 3, animation: `${fadeSlideIn} 0.4s ease-out both` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: xxllncColor.codGray }}>
            {record.display_name}
          </Typography>
          <StatusChip active={record.enabled} />
        </Box>
        {record.description && (
          <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.55)', lineHeight: 1.5, maxWidth: 600 }}>
            {record.description}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
        <StatCard value={mcpCount ?? 0} label={translate('data.statMcpServers')} accent={xxllncColor.dark100} delay={80} />
        <StatCard value={proxyCount ?? 0} label={translate('data.statProxies')} accent={xxllncColor.orange100} delay={120} />
        <StatCard value={headerCount ?? 0} label={translate('data.statHeaders')} accent={xxllncColor.green100} delay={160} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        <DetailCard title={translate('data.details')} delay={200}>
          <InfoRow label={translate('data.name')} value={record.name} mono />
          <InfoRow label={translate('data.authType')} value={translate(AUTH_TYPE_KEYS[record.auth_type] || record.auth_type)} />
          {record.cost_per_1000_calls != null && record.cost_per_1000_calls > 0 && (
            <InfoRow
              label={translate('data.costPer1000Calls')}
              value={
                <Typography component="span" sx={{
                  fontSize: 13, fontWeight: 600,
                  color: xxllncColor.green100, fontVariantNumeric: 'tabular-nums',
                }}>
                  {`\u20AC ${record.cost_per_1000_calls.toFixed(2)}`}
                </Typography>
              }
            />
          )}
        </DetailCard>

        <DetailCard title={translate('data.other')} delay={240}>
          <InfoRow label={translate('data.createdAt')} value={formatDateTime(record.created)} />
          <InfoRow label={translate('data.lastModified')} value={formatDateTime(record.lastModified)} />
        </DetailCard>
      </Box>
    </Box>
  )
}

const ApplicationShow: FC = () => (
  <Show actions={<ShowActions />}>
    <TabbedShowLayout>
      <Tab label="data.details">
        <DetailsTab />
      </Tab>
      <Tab label="resources.application.tabs.environments" path="environments">
        <ApplicationEnvironmentTab />
      </Tab>
      <Tab label="resources.application.tabs.headers" path="headers">
        <ApplicationHeaderTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
)

export default ApplicationShow
